// src/components/NotFound.tsx
import React, { useMemo } from "react";
import { Box, Button, ThemeProvider, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import type { RootState } from "../store";
import { useSelector } from "react-redux";
import Theme from "../utils/theme";

interface NotFoundProps {
    title?: string;
    description?: string;
    primaryLabel?: string;
    onPrimaryClick?: () => void;
    showHomeButton?: boolean;
}

const NotFound: React.FC<NotFoundProps> = ({
    title = "404 – Not found",
    description = "We flapped everywhere but couldn’t find what you were looking for.",
    primaryLabel = "Go back",
    onPrimaryClick,
    showHomeButton = true,
}) => {
    const navigate = useNavigate();

    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const handlePrimaryClick = () => {
        if (onPrimaryClick) {
            onPrimaryClick();
        } else {
            navigate(-1);
        }
    };

    const handleHomeClick = () => {
        navigate("/");
    };

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    minHeight: "60dvh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: 2,
                    gap: 2,
                }}
            >
                {/* Cute CooCoo-ish icon */}
                <Box
                    component="img"
                    src="/images/404NotFound.png"
                    sx={{
                        width: 96,
                        height: 96,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                        boxShadow: 2,
                        bgcolor: (theme) => (theme.palette.mode === "dark" ? "background.paper" : "background.default"),
                    }}
                />

                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        maxWidth: 420,
                        opacity: 0.8,
                    }}
                >
                    {description}
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        mt: 2,
                        flexWrap: "wrap",
                        justifyContent: "center",
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handlePrimaryClick}
                        sx={{
                            borderRadius: 999,
                            px: 3,
                            color: "white",
                        }}
                    >
                        {primaryLabel}
                    </Button>

                    {showHomeButton && (
                        <Button variant="text" onClick={handleHomeClick} sx={{ borderRadius: 999 }}>
                            Back to CooCoo home
                        </Button>
                    )}
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default NotFound;
